import { useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { CalendarIcon } from '@utils/icons';
import { TriggerContent } from '../DatePicker/TriggerContent';
import { ClickableDay } from '../DatePicker/ClickableDay';
import { formatFieldDate } from './fieldDate';
import styles from './DateField.module.css';

interface IDateFieldProps {
  value: Date | null;
  onChange: (next: Date) => void;
  placeholder: string;
  ariaLabel: string;
  minDate?: Date;
  maxDate?: Date;
  /** Specific calendar days to disable (e.g. days the selected entity was absent). */
  disabledDates?: Date[];
  disabled?: boolean;
  elevated?: boolean;
  /**
   * DOM node the calendar portals into. Defaults to `document.body`; pass the
   * host's node when the field sits inside a native-fullscreen subtree (the
   * Fullscreen API paints only the fullscreen element, so a body portal is
   * invisible) — the same contract as `Modal`'s `container`.
   */
  container?: HTMLElement | null;
  /** The day the calendar marks as today, and the month an EMPTY field opens
   *  on. Omit for the browser's own today; a form about a site passes the
   *  SITE's day, which is a different date for part of every day when the
   *  viewer is in another zone. */
  today?: Date | null;
  /** `field` (default): the full bordered field. `icon`: just a calendar
   *  icon button, named by `ariaLabel` — for a toolbar with no room for a field
   *  (the Monitor replay bar's day pills). */
  variant?: 'field' | 'icon';
}

export function DateField({
  value,
  onChange,
  placeholder,
  ariaLabel,
  minDate,
  maxDate,
  disabledDates,
  disabled,
  elevated,
  container,
  today,
  variant = 'field',
}: IDateFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        aria-label={ariaLabel}
        className={variant === 'icon' ? styles.iconTrigger : styles.trigger}
        disabled={disabled}
      >
        {variant === 'icon' ? (
          <CalendarIcon size={13} />
        ) : (
          <TriggerContent display={value ? formatFieldDate(value) : null} placeholder={placeholder} />
        )}
      </Popover.Trigger>
      <Popover.Portal container={container ?? undefined}>
        <Popover.Positioner
          sideOffset={4}
          align="start"
          collisionPadding={8}
          className={elevated ? styles.positioner : undefined}
        >
          <Popover.Popup className={styles.popup}>
            <DayPicker
              mode="single"
              selected={value ?? undefined}
              // Open on the picked day's month, not today's.
              defaultMonth={value ?? today ?? undefined}
              today={today ?? undefined}
              onSelect={(d) => {
                if (d) {
                  onChange(d);
                  setOpen(false);
                }
              }}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
                ...(disabledDates ?? []),
              ]}
              showOutsideDays
              components={{ Day: ClickableDay }}
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
